// Transforma um backup/estado local do legado em linhas validadas, sem tocar na origem.
import { filament, stockItem, product, sale, production, purchase, movement, marketplace, KIND_BY_KEY, isoTs } from '../services/mappers.js';

export const DATA_LISTS = ['products', 'filamentos', 'insumos', 'embalagens', 'outrosItens', 'movimentacoes', 'vendas', 'producoes', 'compras'];

// Mesma validação estrutural de validarBackup() do legado.
export function validateBackup(d) {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return 'Arquivo não é um backup do Print Manager.';
  for (const k of DATA_LISTS) if (d[k] !== undefined && !Array.isArray(d[k])) return `Campo "${k}" inválido (esperado lista).`;
  if (d.settings !== undefined && (typeof d.settings !== 'object' || d.settings === null || Array.isArray(d.settings))) return 'Campo "settings" inválido.';
  if (d.fotos !== undefined && (typeof d.fotos !== 'object' || d.fotos === null)) return 'Campo "fotos" inválido.';
  if (!DATA_LISTS.some(k => d[k] !== undefined)) return 'O arquivo não contém nenhum dado reconhecido.';
  for (const k of ['products', 'filamentos', 'vendas', 'producoes']) if ((d[k] || []).some(x => !x || typeof x !== 'object' || !x.id)) return `Há itens sem identificador em "${k}".`;
  return null;
}

// Equivalente a pmMigrarProdutoVariacoes + pmSyncProduto para produtos antigos.
function normalizeProduct(p) {
  const out = { ...p };
  if (!Array.isArray(out.fotoIds)) out.fotoIds = [];
  if (out.foto && !out.fotoIds.includes('legacy_' + out.id)) { out.fotoIds = [...out.fotoIds, 'legacy_' + out.id]; out.fotoPrincipal = 'legacy_' + out.id; }
  let mats = Array.isArray(out.filamentos) && out.filamentos.length ? out.filamentos : (out.filamentoId && out.peso > 0 ? [{ filamentoId: out.filamentoId, gramasPorUnidade: out.peso }] : []);
  mats = mats.map(m => ({ filamentoId: m.filamentoId, gramasPorUnidade: Number(m.gramasPorUnidade) || 0 }));
  if (!Array.isArray(out.variacoes) || !out.variacoes.length) {
    out.variacoes = [{ id: 'var_' + out.id, nome: 'Padrão', fotoId: out.fotoPrincipal || out.fotoIds[0] || null, filamentos: mats, estoqueAtual: Number(out.estoqueAtual) || 0 }];
  }
  out.variacoes = out.variacoes.map(v => ({ ...v, nome: v.nome || 'Variação', filamentos: Array.isArray(v.filamentos) ? v.filamentos : [], estoqueAtual: Number(v.estoqueAtual) || 0 }));
  delete out.foto;
  return out;
}

const uniqueById = (list, label, warn) => {
  const seen = new Set(), out = [];
  for (const x of list || []) {
    if (!x) continue;
    if (seen.has(x.id)) { warn(`${label}: id repetido ${x.id}; mantida a primeira ocorrência.`); continue; }
    seen.add(x.id); out.push(x);
  }
  return out;
};
const hashStr = s => { let h = 5381; for (let i = 0; i < s.length; i++) h = (h * 33 ^ s.charCodeAt(i)) >>> 0; return h.toString(36); };

export function buildPlan(raw) {
  const err = validateBackup(raw);
  if (err) throw new Error(err);
  const warnings = [], errors = [];
  const warn = m => warnings.push(m);
  const ids = uniqueById;
  const products = ids(raw.products, 'Produtos', warn).map(normalizeProduct);
  const filamentos = ids(raw.filamentos, 'Filamentos', warn);
  const vendas = ids(raw.vendas, 'Vendas', warn);
  const producoes = ids(raw.producoes, 'Produções', warn);
  const compras = ids(raw.compras, 'Compras', warn);
  const lists = { insumos: ids(raw.insumos, 'Insumos', warn), embalagens: ids(raw.embalagens, 'Embalagens', warn), outrosItens: ids(raw.outrosItens, 'Outros', warn) };

  const nameOf = id => (products.find(p => p.id === id) || {}).nome || '';
  const prodIds = new Set(products.map(p => p.id));
  const varIds = new Set(products.flatMap(p => p.variacoes.map(v => v.id)));
  const filIds = new Set(filamentos.map(f => f.id));
  const rows = {
    filaments: filamentos.map(f => ({ id: f.id, ...filament.toRow(f), stock_g: filament.stockOf(f) })),
    stock_items: Object.entries(KIND_BY_KEY).flatMap(([k, kind]) => lists[k].map(i => ({ id: i.id, ...stockItem.toRow({ ...i, nome: i.nome || '(sem nome)' }, kind), stock_qty: stockItem.stockOf(i) }))),
    products: [], sales: [], productions: [], purchases: [], stock_movements: [],
  };
  // Registros-lápide: mantêm o vínculo de vendas/receitas com itens que o legado já apagou.
  const tomb = (id, name) => ({ id, name: name || '(excluído)', extra: { _tombstone: true }, deleted_at: new Date(0).toISOString(), variations: [], images: [] });
  const ghostFil = new Map(), ghostProd = new Map();
  const needProd = (id, nome) => { if (id && !prodIds.has(id) && !ghostProd.has(id)) { ghostProd.set(id, tomb(id, nome)); warn(`Produto ${id} já excluído no legado: histórico preservado.`); } };
  const fixVar = (r, label) => {
    if (r.variation_id && !varIds.has(r.variation_id)) { warn(`${label}: variação ${r.variation_id} não existe mais; nome preservado no histórico.`); r.variation_id = null; }
  };

  for (const p of products) {
    const row = { id: p.id, ...product.toRow(p) };
    p.variacoes.forEach((v, i) => { row.variations[i].stock_qty = Number(v.estoqueAtual) || 0; });
    for (const v of row.variations) {
      v.materials = v.materials.filter(m => {
        if (!m.filament_id) { warn(`Produto ${p.id}: linha de material sem filamento ignorada.`); return false; }
        if (!filIds.has(m.filament_id) && !ghostFil.has(m.filament_id)) ghostFil.set(m.filament_id, { id: m.filament_id, brand: '(excluído)', deleted_at: new Date(0).toISOString(), extra: { _tombstone: true } });
        return true;
      });
    }
    rows.products.push(row);
  }
  for (const v of vendas) {
    const row = { id: v.id, ...sale.toRow(v, nameOf) };
    if (!row.occurred_at) { errors.push(`Venda ${v.id}: data inválida (${v.data}).`); continue; }
    if (!(row.items[0].qty > 0)) { errors.push(`Venda ${v.id}: quantidade inválida.`); continue; }
    if (v.estoqueBaixado === undefined) row.stock_applied = ['confirmada', 'pronta', 'enviada', 'entregue'].includes(v.status);
    if (v.status === undefined) row.status = 'entregue';
    const it = row.items[0];
    needProd(it.product_id); 
    fixVar(it, `Venda ${v.id}`);
    rows.sales.push(row);
  }
  for (const pr of producoes) {
    const copy = !pr.materiais && pr.filamentoId ? { ...pr, materiais: [{ filamentoId: pr.filamentoId, gramas: pr.peso || 0 }] } : pr;
    const row = { id: pr.id, ...production.toRow(copy, nameOf) };
    if (!row.occurred_at) { errors.push(`Produção ${pr.id}: data inválida (${pr.data}).`); continue; }
    if (!(row.qty > 0)) { errors.push(`Produção ${pr.id}: quantidade inválida.`); continue; }
    needProd(row.product_id); fixVar(row, `Produção ${pr.id}`);
    rows.productions.push(row);
  }
  for (const c of compras) {
    const row = { id: c.id, ...purchase.toRow(c) };
    if (!row.purchase_date) { errors.push(`Compra ${c.id}: data inválida (${c.data}).`); continue; }
    rows.purchases.push(row);
  }
  rows.products.push(...ghostProd.values());
  rows.filaments.push(...ghostFil.values());

  // Razão importado em ordem cronológica. Movimento sem id recebe um id determinístico: reimportar não duplica.
  const seenMov = new Set();
  [...(raw.movimentacoes || [])].filter(Boolean).reverse().forEach((m, i) => {
    const id = m.id || 'mig_' + hashStr(JSON.stringify(m)) + '_' + i;
    if (seenMov.has(id)) return;
    seenMov.add(id);
    const row = movement.toRow({ ...m, id });
    if (m.tipo && row.mov_type === 'legado') warn(`Movimento ${id}: tipo "${m.tipo}" preservado como legado.`);
    if (!isoTs(m.data)) warn(`Movimento ${id}: data ausente, usada a data da importação.`);
    rows.stock_movements.push(row);
  });

  const settings = { ...(raw.settings || {}) };
  const platforms = settings.platforms || {};
  delete settings.platforms;
  const marketplaces = Object.entries(platforms).map(([ch, p]) => marketplace.toRow(ch, p));
  const fotos = raw.fotos || {};
  const logo = typeof settings.systemIdentity?.logo === 'string' && settings.systemIdentity.logo.startsWith('data:') ? settings.systemIdentity.logo : null;

  const liveProducts = rows.products.filter(r => !r.deleted_at);
  const counts = {
    products: liveProducts.length, filaments: rows.filaments.filter(r => !r.deleted_at).length, stock_items: rows.stock_items.length,
    sales: rows.sales.length, productions: rows.productions.length, purchases: rows.purchases.length, stock_movements: rows.stock_movements.length,
    product_variations: liveProducts.reduce((s, p) => s + p.variations.length, 0), media_assets: Object.keys(fotos).length,
  };
  const totals = {
    grossRevenue: sum(rows.sales, 'gross_revenue'), netRevenue: sum(rows.sales, 'net_revenue'), profit: sum(rows.sales, 'profit'),
    purchases: sum(rows.purchases, 'amount'), filamentGrams: sum(rows.filaments.filter(r => !r.deleted_at), 'stock_g'),
    productUnits: liveProducts.reduce((s, p) => s + p.variations.reduce((a, v) => a + (v.stock_qty || 0), 0), 0),
  };
  return { rows, settings, marketplaces, fotos, logo, counts, totals, warnings, errors };
}
const sum = (l, k) => Math.round(l.reduce((s, r) => s + (Number(r[k]) || 0), 0) * 100) / 100;

export const ORDER = ['filaments', 'stock_items', 'products', 'sales', 'productions', 'purchases', 'stock_movements'];
const BATCH = 200;

export async function runImport({ plan, ws, hash, mode, api, onProgress = () => {}, batch = BATCH }) {
  const errors = [...plan.errors];
  const run = await api.rpc('import_begin', { p_ws: ws, p_hash: hash, p_mode: mode, p_expected: plan.counts });
  const steps = Object.keys(plan.fotos).length + ORDER.reduce((s, e) => s + plan.rows[e].length, 0) + 1;
  let done = 0; const tick = label => onProgress({ done, total: steps, label });

  const logoHash = {};
  for (const [id, data] of Object.entries(plan.fotos)) {
    tick('Enviando fotografias');
    try { await api.uploadPhoto(ws, id, data); } catch (e) { errors.push(`Foto ${id}: ${e.message}`); }
    done++;
  }
  if (plan.logo) {
    try { await api.uploadPhoto(ws, 'logo', plan.logo, 'logo'); logoHash.ok = true; } catch (e) { errors.push(`Logo: ${e.message}`); }
  }
  for (const entity of ORDER) {
    const list = plan.rows[entity];
    for (let i = 0; i < list.length; i += batch) {
      const slice = list.slice(i, i + batch);
      tick('Gravando ' + entity);
      try { await api.rpc('import_batch', { p_ws: ws, p_run: run, p_entity: entity, p_rows: slice }); }
      catch (e) {
        if (!/fetch|network/i.test(e.message || '') && slice.length > 1) {
          // Isola o registro problemático para os demais seguirem.
          for (const row of slice) {
            try { await api.rpc('import_batch', { p_ws: ws, p_run: run, p_entity: entity, p_rows: [row] }); }
            catch (e2) { errors.push(`${entity} ${row.id}: ${e2.message}`); }
          }
        } else if (slice.length === 1) errors.push(`${entity} ${slice[0].id}: ${e.message}`);
        else throw e;  // rede: interrompe; retomar com o mesmo arquivo continua de onde parou
      }
      done += slice.length;
    }
  }
  const settings = plan.logo ? { ...plan.settings, systemIdentity: { ...plan.settings.systemIdentity, logo: 'media:logo' } } : plan.settings;
  await api.rpc('import_settings', { p_ws: ws, p_run: run, p_settings: settings, p_marketplaces: plan.marketplaces });
  if (mode === 'replace') {
    for (const e of ['filaments', 'stock_items', 'products', 'sales', 'productions', 'purchases']) {
      await api.rpc('import_soft_delete_absent', { p_ws: ws, p_run: run, p_entity: e, p_keep: plan.rows[e].filter(r => !r.deleted_at).map(r => r.id) });
    }
  }
  done++; tick('Validando no servidor');
  const report = await api.rpc('import_finish', { p_ws: ws, p_run: run, p_errors: errors });
  return { ...report, errors, warnings: plan.warnings };
}

// Conferência independente: relê do banco e compara totais financeiros e de estoque com a origem.
export function verifyTotals(plan, entries) {
  const v = JSON.parse(entries.print3d_vendas), c = JSON.parse(entries.print3d_compras), f = JSON.parse(entries.print3d_filamentos), p = JSON.parse(entries.print3d_products);
  const s = (l, k) => Math.round(l.reduce((a, r) => a + (Number(r[k]) || 0), 0) * 100) / 100;
  const got = { grossRevenue: s(v, 'receitaBruta'), netRevenue: s(v, 'receitaLiquida'), profit: s(v, 'lucro'), purchases: s(c, 'valor'), filamentGrams: s(f, 'pesoAtual'), productUnits: p.reduce((a, x) => a + (x.estoqueAtual || 0), 0) };
  const diffs = Object.keys(got).filter(k => Math.abs(got[k] - plan.totals[k]) > 0.05 && !(plan.__mode === 'merge'));
  return { ok: diffs.length === 0, expected: plan.totals, actual: got, diffs };
}

// Lê dados locais SE este navegador/origem os tiver. Só leitura: nunca grava nem apaga nada.
export async function readLocalLegacy(ls = localStorage, idb = indexedDB) {
  const g = k => { try { const v = ls.getItem(k); return v ? JSON.parse(v) : undefined; } catch { return undefined; } };
  const data = { products: g('print3d_products'), settings: g('print3d_settings'), filamentos: g('print3d_filamentos'), insumos: g('print3d_insumos'), embalagens: g('print3d_embalagens'),
    outrosItens: g('print3d_outros'), movimentacoes: g('print3d_mov'), vendas: g('print3d_vendas'), producoes: g('print3d_producoes'), compras: g('print3d_compras') };
  if (!Object.values(data).some(Boolean)) return null;
  for (const k of Object.keys(data)) if (data[k] === undefined) delete data[k];
  data.fotos = await new Promise(res => {
    const databases = idb.databases ? idb.databases() : Promise.resolve([{ name: 'Print3DFotos' }]);
    databases.then(list => {
      if (!list.some(d => d.name === 'Print3DFotos')) return res({});
      const r = idb.open('Print3DFotos');
      r.onerror = () => res({});
      r.onsuccess = () => {
        const db = r.result;
        if (!db.objectStoreNames.contains('fotos')) return res({});
        const out = {}, c = db.transaction('fotos').objectStore('fotos').openCursor();
        c.onsuccess = () => { const cur = c.result; if (cur) { out[cur.key] = cur.value; cur.continue(); } else res(out); };
        c.onerror = () => res(out);
      };
    }).catch(() => res({}));
  });
  return data;
}
